import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Share2, Twitter, Facebook, Linkedin, MessageCircle, Mail, Link2, Instagram } from "lucide-react";
import { toast } from "sonner";

/**
 * Share menu for a certified property. Prefills captions with the property's
 * freshness score to encourage organic distribution of Guest Smell Certified™.
 */
export default function SharePropertyMenu({ property }) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const score = property?.freshness_score ?? 0;
  const name = property?.name || "this stay";
  const caption = `Booking with confidence — ${name} is Guest Smell Certified™ with a ${score}/100 freshness score. See it. Smell it. Book it.`;
  const captionShort = `${name} — ${score}/100 Guest Smell Certified™`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${caption}\n${url}`);
      toast.success("Copied caption + link");
    } catch { toast.error("Could not copy"); }
  };

  const open = (u) => window.open(u, "_blank", "noopener,noreferrer,width=600,height=640");

  const share = {
    twitter: () => open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(url)}`),
    facebook: () => open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(caption)}`),
    linkedin: () => open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`),
    whatsapp: () => open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${caption} ${url}`)}`),
    email: () => { window.location.href = `mailto:?subject=${encodeURIComponent(captionShort)}&body=${encodeURIComponent(`${caption}\n\n${url}`)}`; },
    instagram: async () => {
      try {
        await navigator.clipboard.writeText(`${caption}\n${url}`);
        toast.success("Caption copied — paste into Instagram");
      } catch { toast.error("Could not copy caption"); }
    },
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full" data-testid="share-menu-trigger">
          <Share2 className="w-4 h-4 mr-1" /> Share
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={share.twitter} data-testid="share-twitter">
          <Twitter className="w-4 h-4 mr-2 text-sky-500" /> Post to X (Twitter)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={share.facebook} data-testid="share-facebook">
          <Facebook className="w-4 h-4 mr-2 text-blue-600" /> Share on Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={share.linkedin} data-testid="share-linkedin">
          <Linkedin className="w-4 h-4 mr-2 text-blue-700" /> Share on LinkedIn
        </DropdownMenuItem>
        <DropdownMenuItem onClick={share.whatsapp} data-testid="share-whatsapp">
          <MessageCircle className="w-4 h-4 mr-2 text-emerald-500" /> WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onClick={share.instagram} data-testid="share-instagram">
          <Instagram className="w-4 h-4 mr-2 text-pink-600" /> Instagram (copy caption)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={share.email} data-testid="share-email">
          <Mail className="w-4 h-4 mr-2 text-slate-600" /> Email a friend
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={copy} data-testid="share-copy">
          <Link2 className="w-4 h-4 mr-2 text-slate-600" /> Copy caption + link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
